import React, { useMemo } from 'react';
import {
	Flex,
	Image,
	Link as ALink,
	Modal,
	ModalBody,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Text,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { useBlockchainProvider } from '../../providers/Blockchain/blockchainProvider';
import { CloseIcon } from '@chakra-ui/icons';
import { LanguageChange } from '../LanguageChange/languageChange';
import { Socials } from '../Socials/socials';
import { WalletElement } from '../WalletElement/walletElement';
import { t } from 'i18next';
import { containerMaxWXl } from '../ContentContainer/contentContainer';
import { NetworkPane } from './NetworkPane/network';
import Vendor from '../../vendor.config';
import { Color } from 'src/theme/color';

export const Menu = ({ onCloseMobileMenu, isOpenMobileMenu, mainColor }: any) => {
	const { isLoggedIn } = useBlockchainProvider();

	const menuItems: ReadonlyArray<{
		name?: string;
		link: string;
		customAsset?: { element: JSX.Element; title: string };
	}> = useMemo(
		() =>
			[
				{
					name: t('menu.gamExplorer').toString(),
					link: '/',
				},
				{
					name: t('menu.library').toString(),
					link: '/games',
				},
				{
					link: 'https://GameContract.io',
					customAsset: {
						element: (
							<Flex>
								<Text>Game</Text>
								<Text
									fontWeight="600"
									color="action"
								>
									Contract
								</Text>
							</Flex>
						),
						title: 'Add your game',
					},
				},
				{
					link: 'https://GameCap.io',
					customAsset: {
						element: (
							<Flex>
								<Text>Game</Text>
								<Text
									fontWeight="600"
									color="action"
								>
									Cap
								</Text>
							</Flex>
						),
						title: 'Games ranking',
					},
				},
			] as const,
		[isLoggedIn]
	);

	return (
		<>
			<Modal
				size="full"
				isOpen={isOpenMobileMenu}
				onClose={onCloseMobileMenu}
			>
				<ModalOverlay />
				<ModalContent
					bg={Color.BACKGROUND}
					p="32px"
				>
					<ModalHeader>
						<Flex
							alignItems="center"
							justifyContent="space-between"
						>
							<Link to="/">
								<Image
									maxW="33px"
									maxH="33px"
									src={Vendor.gui.asset.logoSmall}
								/>
							</Link>
							<Flex
								cursor="pointer"
								onClick={onCloseMobileMenu}
								fontWeight="600"
								fontSize="16px"
								gridGap="14px"
								alignItems="center"
							>
								<Text>Close</Text>
								<CloseIcon color={mainColor || Color.ACTION} />
							</Flex>
						</Flex>
					</ModalHeader>
					<ModalBody>
						<Flex
							w="100%"
							flexDirection="column"
							gridGap="68px"
							alignItems="center"
							fontWeight="400"
						>
							<LanguageChange />
							<Flex
								flexDirection="column"
								justifyContent="baseline"
								gridGap="41px"
							>
								{menuItems.map(item =>
									/(http(s?)):\/\//i.test(item.link) ? (
										<ALink
											_hover={{ textDecoration: 'none' }}
											key={item.link}
											href={item.link}
											outline="unset"
										>
											{item.customAsset ? (
												<Flex
													direction="column"
													align="start"
													position="relative"
												>
													<Text
														fontWeight="700"
														color="#878787"
														fontSize="8px"
														lineHeight="10px"
														position="absolute"
														top="calc(-1em - 2px)"
														left="0"
													>
														{item.customAsset.title}
													</Text>
													{item.customAsset.element}
												</Flex>
											) : (
												<Text
													cursor="pointer"
													textAlign="center"
													_hover={{ opacity: '1' }}
												>
													{item.name}
												</Text>
											)}
										</ALink>
									) : (
										<Link
											key={item.link}
											to={item.link}
										>
											{item.customAsset ? (
												<Flex
													direction="column"
													align="start"
													position="relative"
												>
													<Text
														fontWeight="700"
														color="#878787"
														fontSize="8px"
														lineHeight="10px"
														position="absolute"
														top="calc(-1em - 2px)"
														left="0"
													>
														{item.customAsset.title}
													</Text>
													{item.customAsset.element}
												</Flex>
											) : (
												<Text
													cursor="pointer"
													textAlign="center"
													_hover={{ opacity: '1' }}
												>
													{item.name}
												</Text>
											)}
										</Link>
									)
								)}
							</Flex>
						</Flex>
					</ModalBody>
					<ModalFooter
						flexWrap="wrap"
						gridGap={{ base: '15px', md: '40px' }}
						justifyContent="center"
						display="flex"
						fontSize="12px"
						fontWeight="600"
					>
						<Flex
							w="100%"
							justifyContent="center"
						>
							<Image
								src="/icons/hodl.svg"
								alt="hodl"
							/>
						</Flex>
						<Socials hasDescription />
					</ModalFooter>
				</ModalContent>
			</Modal>
			<Flex
				py="18px"
				px={{ base: '16px', hashup: '32px', xl: `calc((100% - ${containerMaxWXl}) / 2)` }}
				display={{ base: 'none', hashup: 'flex' }}
				justifyContent="space-between"
				flexWrap="wrap"
				userSelect="none"
			>
				<Flex
					alignItems="center"
					gridGap="72px"
				>
					<Link to="/">
						<Image
							w="200px"
							src={Vendor.gui.asset.logo}
						/>
					</Link>
					<Flex
						alignItems="center"
						gridGap="34px"
						color="text"
						fontWeight="400"
						fontSize="16px"
						lineHeight="20px"
					>
						{menuItems.map(item =>
							/(http(s?)):\/\//i.test(item.link) ? (
								<ALink
									key={item.link}
									href={item.link}
									_hover={{ textDecoration: 'none' }}
									outline="unset"
								>
									{item.customAsset ? (
										<Flex
											direction="column"
											align="start"
											position="relative"
										>
											<Text
												fontWeight="700"
												color="#878787"
												fontSize="8px"
												lineHeight="10px"
												position="absolute"
												top="calc(-1em - 2px)"
												left="0"
											>
												{item.customAsset.title}
											</Text>
											{item.customAsset.element}
										</Flex>
									) : (
										<Text
											cursor="pointer"
											textAlign="center"
											_hover={{ opacity: '1' }}
										>
											{item.name}
										</Text>
									)}
								</ALink>
							) : (
								<Link
									key={item.link}
									to={item.link}
								>
									{item.customAsset ? (
										<Flex
											direction="column"
											align="start"
											position="relative"
										>
											<Text
												fontWeight="700"
												color="#878787"
												fontSize="8px"
												lineHeight="10px"
												position="absolute"
												top="calc(-1em - 2px)"
												left="0"
											>
												{item.customAsset.title}
											</Text>
											{item.customAsset.element}
										</Flex>
									) : (
										<Text
											cursor="pointer"
											textAlign="center"
											_hover={{ opacity: '1' }}
										>
											{item.name}
										</Text>
									)}
								</Link>
							)
						)}
					</Flex>
				</Flex>
				<Flex
					gap="24px"
					alignItems="center"
				>
					<NetworkPane />
					<WalletElement
						isMobile={false}
						t={t}
					/>
				</Flex>
			</Flex>
		</>
	);
};
